/**
 * Does a house font stay on the customer's own site?
 *
 * This is the whole point of letting the banner load a font at all. Same-origin means the
 * request carries nothing that loading the page had not already sent; a face from Google
 * Fonts or a foreign CDN is a third-party call made before the visitor has decided
 * anything, which is the one thing the SDK exists to prevent.
 *
 * A relative path is the safe form and the one the console recommends: a site usually
 * answers on several hosts — the apex, www, staging — and `/fonts/x.woff2` is same-origin
 * on every one of them. `https://kunde.de/fonts/x.woff2` is same-origin on exactly one; on
 * www.kunde.de it is a third-party request that happens to look harmless.
 *
 * A warning, not a block: the domain list is what the console knows, and a customer may
 * well be serving the font from a host they have not registered here. The server refuses
 * only what can never be same-origin at all (see validate.go).
 */

/** Hosts are compared exactly. A subdomain is a different origin, not a near miss. */
const ABSOLUTE = /^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/?#]+)/i;

/**
 * The foreign host this URL points at, or null when it is fine.
 *
 * null covers both the relative path (same-origin everywhere) and the absolute URL whose
 * host is one of the site's own.
 */
export function foreignFontHost(url: string, domains: string[]): string | null {
  const m = ABSOLUTE.exec(url.trim());
  if (!m) return null;
  // Strip credentials and port: neither is part of the host a domain list names, and
  // "kunde.de:443" must not read as a stranger.
  const host = m[1]!.toLowerCase().replace(/^[^@]*@/, '').replace(/:\d+$/, '');
  return domains.some((d) => d.trim().toLowerCase() === host) ? null : host;
}
